import { gql, TypedDocumentNode } from "@apollo/client";

import {
  Maybe,
  UserGuide,
  UserGuideGroup,
  UserGuideChapter,
  UserGuideMetadata,
  UserGuideProgress,
  UserGuideStep,
  UserGuideCompletion,
  UserGuideCertification,
  QueryUserGuideGroupArgs,
  MutationUserGuideStartArgs,
  MutationUserGuideRestartArgs,
  MutationUserGuideCertificationCreateArgs,
  MutationUserGuideCertificationUpdateArgs,
} from "types/generated";

type FetchUserGuideGroupsData = {
  userGuideGroups: (Pick<UserGuideGroup, "id" | "name" | "description" | "skillLevel" | "order"> & {
    chapters: Array<{
      guides: Array<
        Pick<UserGuide, "id"> & { metadata: Pick<UserGuideMetadata, "minutesToComplete"> }
      >;
    }>;
  })[];
  completedUserGuides: Array<{
    guide: Pick<UserGuide, "id">;
  }>;
};

export const GET_USER_GUIDE_GROUPS: TypedDocumentNode<FetchUserGuideGroupsData> = gql`
  query GetUserGuideGroups {
    userGuideGroups {
      id
      name
      description
      skillLevel
      order
      chapters {
        guides {
          id
          metadata {
            minutesToComplete
          }
        }
      }
    }
    completedUserGuides {
      guide {
        id
      }
    }
  }
`;

type FetchMissionControlData = {
  userGuideGroups: (Pick<UserGuideGroup, "id" | "name" | "order"> & {
    chapters: Array<
      Pick<UserGuideChapter, "order"> & {
        guides: Array<
          Pick<UserGuide, "id" | "order"> & {
            metadata: Pick<UserGuideMetadata, "title" | "minutesToComplete">;
          }
        >;
      }
    >;
  })[];
  completedUserGuides: Array<{
    guide: Pick<UserGuide, "id">;
  }>;
  activeUserGuideProgress: Maybe<{
    guide: Pick<UserGuide, "id">;
    currentStep: UserGuideProgress["currentStep"];
  }>;
};

export const GET_MISSION_CONTROL_DATA: TypedDocumentNode<FetchMissionControlData> = gql`
  query GetMissionControlData {
    userGuideGroups {
      id
      name
      order
      chapters {
        order
        guides {
          id
          order
          metadata {
            title
            minutesToComplete
          }
        }
      }
    }
    completedUserGuides {
      guide {
        id
      }
    }
    activeUserGuideProgress {
      guide {
        id
      }
      currentStep
    }
  }
`;

type FetchUserGuideGroupData = {
  userGuideGroup: Maybe<
    Pick<UserGuideGroup, "id" | "name" | "description" | "skillLevel" | "requiredEntitlements"> & {
      chapters: Array<
        Pick<UserGuideChapter, "id" | "name" | "description"> & {
          guides: Array<
            Pick<UserGuide, "id"> & {
              metadata: Pick<
                UserGuideMetadata,
                "title" | "description" | "prerequisites" | "minutesToComplete"
              >;
              prerequisiteGuides: {
                all: Array<Pick<UserGuide, "id">>;
                completed: Array<Pick<UserGuide, "id">>;
              };
              steps: Array<Pick<UserGuideStep, "id" | "order">>;
            }
          >;
        }
      >;
    }
  >;
  activeUserGuideProgress: Maybe<{
    guide: Pick<UserGuide, "id">;
    currentStep: UserGuideProgress["currentStep"];
  }>;
  completedUserGuides: Array<{
    guide: Pick<UserGuide, "id">;
  }>;
};

export const GET_USER_GUIDE_GROUP: TypedDocumentNode<
  FetchUserGuideGroupData,
  QueryUserGuideGroupArgs
> = gql`
  query GetUserGuideGroup($id: ID!) {
    userGuideGroup(id: $id) {
      id
      name
      description
      skillLevel
      requiredEntitlements
      chapters {
        id
        name
        description
        guides {
          id
          metadata {
            title
            description
            prerequisites
            minutesToComplete
          }
          prerequisiteGuides {
            all {
              id
            }
            completed {
              id
            }
          }
          steps {
            id
            order
          }
        }
      }
    }
    activeUserGuideProgress {
      guide {
        id
      }
      currentStep
    }
    completedUserGuides {
      guide {
        id
      }
    }
  }
`;

export type UserGuideStepData = Pick<
  UserGuideStep,
  "id" | "title" | "instruction" | "hint" | "order"
>;

type FetchUserGuideProgressData = {
  activeUserGuideProgress: Maybe<{
    guide: Pick<UserGuide, "id"> & {
      metadata: Pick<UserGuideMetadata, "title" | "description" | "prerequisites">;
      steps: Array<UserGuideStepData>;
      completion: UserGuideCompletion;
    };
    currentStep: UserGuideProgress["currentStep"];
  }>;
  completedUserGuides: Array<{
    guide: Pick<UserGuide, "id">;
  }>;
};

export const GET_USER_GUIDE_PROGRESS: TypedDocumentNode<FetchUserGuideProgressData> = gql`
  query GetUserGuideProgress {
    activeUserGuideProgress {
      guide {
        id
        metadata {
          title
          description
          prerequisites
        }
        steps {
          id
          title
          instruction
          hint
          order
        }
        completion {
          recommendedGuideIds
          successMessage
        }
      }
      currentStep
    }
    completedUserGuides {
      guide {
        id
      }
    }
  }
`;

type UserGuideStartData = {
  userGuideStart: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_START: TypedDocumentNode<UserGuideStartData, MutationUserGuideStartArgs> =
  gql`
    mutation UserGuideStart($id: ID!) {
      userGuideStart(id: $id) {
        status
      }
    }
  `;

type UserGuideMoveToNextStepData = {
  userGuideMoveToNextStep: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_MOVE_TO_NEXT_STEP: TypedDocumentNode<UserGuideMoveToNextStepData> =
  gql`
    mutation UserGuideMoveToNextStep {
      userGuideMoveToNextStep {
        status
      }
    }
  `;

type UserGuideMoveToPreviousStepData = {
  userGuideMoveToPreviousStep: Pick<UserGuideProgress, "status">;
};
export const USER_GUIDE_MOVE_TO_PREVIOUS_STEP: TypedDocumentNode<
  UserGuideMoveToPreviousStepData
> = gql`
  mutation UserGuideMoveToPreviousStep {
    userGuideMoveToPreviousStep {
      status
    }
  }
`;

type UserGuideCompleteData = {
  userGuideComplete: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_COMPLETE: TypedDocumentNode<UserGuideCompleteData> = gql`
  mutation UserGuideComplete {
    userGuideComplete {
      status
    }
  }
`;

type UserGuideAbandonData = {
  userGuideAbandon: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_ABANDON: TypedDocumentNode<UserGuideAbandonData> = gql`
  mutation UserGuideAbandon {
    userGuideAbandon {
      status
    }
  }
`;

type UserGuideRestartData = {
  userGuideRestart: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_RESTART: TypedDocumentNode<
  UserGuideRestartData,
  MutationUserGuideRestartArgs
> = gql`
  mutation UserGuideRestart($id: ID!) {
    userGuideRestart(id: $id) {
      status
    }
  }
`;

type UserGuideCertificateFields = Pick<
  UserGuideCertification,
  "id" | "groupID" | "course" | "displayName" | "issuedAt"
>;

type FetchUserGuideCertificationListData = {
  userGuideCertificationList: UserGuideCertificateFields[];
};

export const GET_USER_GUIDE_CERTIFICATION_LIST: TypedDocumentNode<
  FetchUserGuideCertificationListData
> = gql`
  query GetUserGuideCertificationList {
    userGuideCertificationList {
      id
      groupID
      course
      displayName
      issuedAt
    }
  }
`;

type UserGuideCertificationCreateData = {
  userGuideCertificationCreate: UserGuideCertificateFields;
};

export const USER_GUIDE_CERTIFICATION_CREATE: TypedDocumentNode<
  UserGuideCertificationCreateData,
  MutationUserGuideCertificationCreateArgs
> = gql`
  mutation UserGuideCertificationCreate($groupID: String!, $displayName: String!) {
    userGuideCertificationCreate(groupID: $groupID, displayName: $displayName) {
      id
      groupID
      course
      displayName
      issuedAt
    }
  }
`;

type UserGuideCertificationUpdateData = {
  userGuideCertificationUpdate: UserGuideCertificateFields;
};

export const USER_GUIDE_CERTIFICATION_UPDATE: TypedDocumentNode<
  UserGuideCertificationUpdateData,
  MutationUserGuideCertificationUpdateArgs
> = gql`
  mutation UserGuideCertificationUpdate($id: ID!, $displayName: String!) {
    userGuideCertificationUpdate(id: $id, displayName: $displayName) {
      id
      groupID
      course
      displayName
      issuedAt
    }
  }
`;
